import { getMomentTz } from "@dexteel/mesf-core";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import type { UtilityConsumptionPeriod } from "../../models/UtilityConsumption";
import { formatVolume } from "../../utils/format";
import {
	CONSUMPTION_CAPTION_SX,
	CONSUMPTION_UNIT_SX,
} from "../../utils/tokens";

const moment = getMomentTz();

interface ConsumptionChartHeaderProps {
	period: UtilityConsumptionPeriod;
	isPartial: boolean;
	total: number | null;
	unit: string;
	meterScopeLabel: string | null;
}

export const ConsumptionChartHeader = ({
	period,
	isPartial,
	total,
	unit,
	meterScopeLabel,
}: ConsumptionChartHeaderProps) => {
	const startUtc = moment(period.startUtc).format("MMM D, YYYY HH:mm");
	const endUtc = moment(period.endUtc).format("MMM D, YYYY HH:mm");
	const periodText = `${startUtc} - ${endUtc}`;

	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				alignItems: "flex-start",
				justifyContent: "space-between",
				gap: 1,
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Typography variant="subtitle1" fontWeight={600}>
					Period consumption
				</Typography>
				<Typography component="div" sx={CONSUMPTION_CAPTION_SX}>
					{periodText}
					{meterScopeLabel !== null && ` · Meter: ${meterScopeLabel}`}
				</Typography>
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					gap: 1,
					ml: "auto",
				}}
			>
				{isPartial && (
					<Tooltip title="The period has not closed yet: readings only reach part of it, so the total is not final.">
						<Chip
							label="Partial period"
							size="small"
							color="warning"
							sx={{ fontWeight: 600 }}
						/>
					</Tooltip>
				)}
				<Box sx={{ alignItems: "baseline", display: "flex", gap: 0.5 }}>
					<Typography
						component="span"
						sx={{
							fontSize: "1.75rem",
							fontVariantNumeric: "tabular-nums",
							fontWeight: 600,
							lineHeight: 1,
						}}
					>
						{formatVolume(total) ?? "-"}
					</Typography>
					{total !== null && (
						<Typography component="span" sx={CONSUMPTION_UNIT_SX}>
							{unit}
						</Typography>
					)}
				</Box>
			</Box>
		</Box>
	);
};
